import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { BreadcrumbObjectType, useNavigationItemsQuery } from '~/common/generated/apollo-operations'
import { usePage } from '~/common/hooks/usePage'
import type { NavigationItem } from '~/components/left-nav/configbase-nav/ConfigbaseNav'
import { useConfigbaseNavEnabled } from '~/components/left-nav/configbase-nav/useConfigbaseNavEnabled'

/** The app page an embed route has mounted, as the frame reports it. */
interface EmbedPage {
  embedId: string
  embedName: string | null
  pageTitle: string
}

interface EmbedTitleContextType {
  page: EmbedPage | null
  setPage: (page: EmbedPage | null) => void
}

const EmbedTitleContext = createContext<EmbedTitleContextType | undefined>(undefined)

/** Carries the mounted app's page title from the embed route to the shell's title bar and tab. */
export const EmbedTitleProvider = ({ children }: { children: ReactNode }) => {
  const [page, setPage] = useState<EmbedPage | null>(null)
  return (
    <EmbedTitleContext.Provider value={{ page, setPage }}>{children}</EmbedTitleContext.Provider>
  )
}

const useEmbedTitleContext = (): EmbedTitleContextType => {
  const context = useContext(EmbedTitleContext)
  if (!context) {
    throw new Error('useEmbedTitle must be used within an EmbedTitleProvider')
  }
  return context
}

/** The label of the nav item opening an app's root: the org's name for that app. */
export const getAppLabelForEmbed = (
  items: readonly NavigationItem[],
  embedId: string,
): string | null => {
  const root = `/embed/${embedId}`
  const item = items.find((candidate) => {
    if (candidate.href === null) {
      return false
    }
    const { pathname, search } = new URL(candidate.href, 'https://navigation.invalid')
    return pathname === root && search === ''
  })
  return item?.label ?? null
}

/** "App: Page", or the app alone when the page has no title of its own. */
export const composeEmbedTitle = (appLabel: string, pageTitle: string): string =>
  pageTitle === '' || pageTitle === appLabel ? appLabel : `${appLabel}: ${pageTitle}`

/** Publish an embed route's page, clearing it when the route unmounts. */
export const usePublishEmbedPage = (page: EmbedPage | undefined): void => {
  const { setPage } = useEmbedTitleContext()
  const embedId = page?.embedId
  const embedName = page?.embedName ?? null
  const pageTitle = page?.pageTitle ?? ''
  // Share the frame's page title with the shell while this embed route is mounted
  useEffect(() => {
    if (embedId === undefined) {
      return
    }
    setPage({ embedId, embedName, pageTitle })
    return () => {
      setPage(null)
    }
  }, [embedId, embedName, pageTitle, setPage])
}

/** The mounted app's own page title, or `''` off embed pages. */
export const useEmbedPageTitle = (): string => {
  const { page } = useEmbedTitleContext()
  const { objectId } = usePage()
  return page !== null && page.embedId === objectId ? page.pageTitle : ''
}

/**
 * An embed page's title under configbase navigation: the org's nav label for the app (else the
 * embed name), then the page title the app declares. `null` off embed pages or with the flag off.
 */
export const useEmbedTitle = (): string | null => {
  const { isEnabled: isConfigbaseNavEnabled } = useConfigbaseNavEnabled()
  const { page } = useEmbedTitleContext()
  const { objectId, objectType } = usePage()
  const isEnabled =
    isConfigbaseNavEnabled &&
    objectType === BreadcrumbObjectType.EMBED &&
    typeof objectId === 'string'
  const { data } = useNavigationItemsQuery({ skip: !isEnabled })
  if (!isEnabled) {
    return null
  }
  const current = page?.embedId === objectId ? page : null
  const appLabel =
    getAppLabelForEmbed(data?.navigationItems ?? [], objectId) ?? current?.embedName ?? null
  return appLabel === null ? null : composeEmbedTitle(appLabel, current?.pageTitle ?? '')
}
